<template>
	<div :class="rootClasses">
		<div class="app-tag-selector__header">
			<div class="app-tag-selector__search">
				<app-input
					ref="searchInputRef"
					v-model="searchQuery"
					:placeholder="placeholder || $t('search_short')"
					show-clear-button
					@clear-field="clearSearch"
				>
					<template #custom-icons>
						<app-tooltip
							v-if="props.allowCreate && props.mode === 'select' && searchQuery"
							:text="$t('file_add_tag')"
							position="top"
						>
							<template #activator>
								<app-icon
									name="plus"
									width="16px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
									@click="createTag"
								/>
							</template>
						</app-tooltip>
					</template>
				</app-input>
				<div
					class="app-tag-selector__close"
					@click="emit('close')"
				>
					<app-icon
						class="app-tag-selector__close-icon"
						name="close"
						width="20px"
						color="#979797"
						hover-color="#499259"
						cursor="pointer"
					/>
				</div>
			</div>

		</div>

		<span
			v-show="!searchQuery && hasSelection"
			class="app-tag-selector__reset"
			@click="resetSelection"
		>
			{{ $t("selector_reset") }}
		</span>

		<div
			v-if="loading"
			class="app-tag-selector__body app-tag-selector__body--loading"
		>
			<app-loader />
		</div>

		<!-- Мод filter -->
		<div
			v-else-if="mode === 'filter'"
			class="app-tag-selector__body"
		>
			<template v-if="visibleCategories.length > 0">
				<div
					v-for="category in visibleCategories"
					:key="category.key"
					class="app-tag-selector__section"
				>
					<div class="app-tag-selector__section-title">
						<div class="app-tag-selector__section-title-text">
							{{ category.label }}
						</div>
					</div>
					<app-tag-selector-list
						:tags="getFilteredTags(category)"
						:selected-ids="selectedIds"
						:search-text="debouncedQuery"
						@toggle="toggleTag"
					/>
				</div>
			</template>

			<div v-else class="app-tag-selector__empty">
				{{ hasAnyTags ? $t("ui_tag_not_found") : $t("ui_tags_empty") }}
			</div>
		</div>

		<!-- Мод select (трехсекционный) -->
		<div
			v-else
			class="app-tag-selector__body app-tag-selector__body--columns"
		>
			<template v-if="visibleCategories.length > 0">
				<div class="app-tag-selector__columns">
					<div
						v-if="systemCategory && hasVisibleTags(systemCategory)"
						class="app-tag-selector__section app-tag-selector__section--system"
					>
						<div class="app-tag-selector__section-title">
							<app-checkbox
								v-if="allowSelectGroup"
								:style="{ height: '16px', marginRight: '8px' }"
								:value="isCategoryAllSelected(systemCategory)"
								text=""
								@on-input="toggleCategory(systemCategory)"
							/>
							<div
								class="app-tag-selector__section-title-text"
								@click="allowSelectGroup ? toggleCategory(systemCategory) : undefined"
							>
								{{ systemCategory.label }}
							</div>
						</div>
						<app-tag-selector-list
							:tags="getFilteredTags(systemCategory)"
							:selected-ids="selectedIds"
							:search-text="debouncedQuery"
							@toggle="toggleTag"
						/>
					</div>

					<div
						v-if="(entityCategory && hasVisibleTags(entityCategory)) || (userCategory && hasVisibleTags(userCategory))"
						class="app-tag-selector__section-right"
					>
						<div
							v-if="entityCategory && hasVisibleTags(entityCategory)"
							:class="{
								'app-tag-selector__section': true,
								'app-tag-selector__section--entity': true,
								'app-tag-selector__section--full': !userCategory || !hasVisibleTags(userCategory)
							}"
						>
							<div class="app-tag-selector__section-title">
								<app-checkbox
									v-if="allowSelectGroup"
									:style="{ height: '16px', marginRight: '8px' }"
									:value="isCategoryAllSelected(entityCategory)"
									text=""
									@on-input="toggleCategory(entityCategory)"
								/>
								<div
									class="app-tag-selector__section-title-text"
									@click="allowSelectGroup ? toggleCategory(entityCategory) : undefined"
								>
									{{ entityCategory.label }}
								</div>
							</div>
							<app-tag-selector-list
								:tags="getFilteredTags(entityCategory)"
								:selected-ids="selectedIds"
								:search-text="debouncedQuery"
								@toggle="toggleTag"
							/>
						</div>

						<div
							v-if="userCategory && hasVisibleTags(userCategory)"
							:class="{
								'app-tag-selector__section': true,
								'app-tag-selector__section--user': true,
								'app-tag-selector__section--full': !entityCategory || !hasVisibleTags(entityCategory)
							}"
						>
							<div class="app-tag-selector__section-title">
								<app-checkbox
									v-if="allowSelectGroup"
									:style="{ height: '16px', marginRight: '8px' }"
									:value="isCategoryAllSelected(userCategory)"
									text=""
									@on-input="toggleCategory(userCategory)"
								/>
								<div
									class="app-tag-selector__section-title-text"
									@click="allowSelectGroup ? toggleCategory(userCategory) : undefined"
								>
									{{ userCategory.label }}
								</div>
							</div>
							<app-tag-selector-list
								:tags="getFilteredTags(userCategory)"
								:selected-ids="selectedIds"
								:search-text="debouncedQuery"
								@toggle="toggleTag"
							/>
						</div>
					</div>
				</div>
			</template>

			<div v-else class="app-tag-selector__empty">
				{{ hasAnyTags ? $t("ui_tag_not_found") : $t("ui_tags_empty") }}
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	toRef,
	computed,
	watch,
	onMounted,
	nextTick
} from 'vue';
import { debounce } from 'lodash';
import AppCheckbox from '@/components/ui-kit/checkbox/AppCheckbox.vue';
import AppTagSelectorList from './AppTagSelectorList.vue';
import AppInput from '@/components/ui-kit/input/AppInput.vue';
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue';
import AppLoader from '@/components/common/AppLoader.vue';
import AppTooltip from '@/components/ui-kit/tooltip/AppTooltip.vue';
import type { TagWithFrequency, TagContainer } from '@/models/interfaces/tags/tag-models';
import type { TagCategory } from './types';
import { TagEntityType } from '@/models/etc/types';
import { useTagList } from '@/composables/useTagsCache';
import { i18n } from '@/i18n';

const entityLabelKeys: Record<number, string> = {
	[TagEntityType.Person]: 'tags_of_contacts',
	[TagEntityType.Client]: 'tags_of_clients',
	[TagEntityType.Project]: 'tags_of_projects',
	[TagEntityType.File]: 'tags_of_files',
	[TagEntityType.Job]: 'tags_of_jobs',
};

type AppTagSelectorProps = {
	modelValue?: TagWithFrequency[];
	categories?: TagCategory[];
	entityType?: TagEntityType;
	placeholder?: string;
	mode?: 'filter' | 'select';
	allowCreate?: boolean;
	allowSelectGroup?: boolean;
};

type AppTagSelectorEmits = {
	'update:modelValue': [tags: TagWithFrequency[]];
	'reset': [];
	'close': [];
	'create-tag': [name: string];
};

const props = withDefaults(defineProps<AppTagSelectorProps>(), {
	modelValue: () => [],
	categories: () => [],
	placeholder: '',
	mode: 'filter',
	allowCreate: false,
	allowSelectGroup: false,
});

const emit = defineEmits<AppTagSelectorEmits>();

const searchQuery = ref('');
const debouncedQuery = ref('');

const searchInputRef = ref<typeof AppInput | null>(null);

onMounted(async () => {
	await nextTick();
	searchInputRef.value?.focusInput();
});

// список тегов держит общий кеш: при повторном открытии он отдаётся сразу,
// а свежие данные подтягиваются фоном и вливаются точечно
const { container: tagContainer, loading } = useTagList(toRef(props, 'entityType'));

const applySearchQuery = debounce((value: string) => {
	debouncedQuery.value = value;
}, 300);

watch(searchQuery, value => applySearchQuery(value));

const clearSearch = () => {
	applySearchQuery.cancel();
	searchQuery.value = '';
	debouncedQuery.value = '';
};

const buildCategoriesFromContainer = (container: TagContainer): TagCategory[] => {
	const labelKey = entityLabelKeys[props.entityType!] || 'ui_all_tags';
	const label = String(i18n.global.t(labelKey));
	if (props.mode === 'filter') {
		return [{ key: 'entity', label, tags: container.OftenUsedForEntityTags }];
	}
	return [
		{ key: 'system', label: String(i18n.global.t('selector_system_tags')), tags: container.AllSystemTags },
		{ key: 'entity', label, tags: container.OftenUsedForEntityTags },
		{ key: 'user', label: String(i18n.global.t('ui_all_your_tags')), tags: container.OftenUsedByUserTags },
	];
};

const resolvedCategories = computed<TagCategory[]>(() => {
	if (props.entityType != null) {
		return buildCategoriesFromContainer(tagContainer.value);
	}
	return props.categories;
});

const rootClasses = computed(() => ({
	'app-tag-selector': true,
	'app-tag-selector--select': props.mode === 'select',
}));

const selectedIds = computed(() => new Set(props.modelValue.map(t => t.Id)));

const hasSelection = computed(() => props.modelValue.length > 0);

const systemCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'system')
);

const entityCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'entity')
);

const userCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'user')
);

const visibleCategories = computed(() =>
	resolvedCategories.value.filter(c => hasVisibleTags(c))
);

const matchesSearch = (tag: TagWithFrequency, query: string): boolean => {
	if (!query) return true;
	const lowerQuery = query.toLowerCase();
	const name = (tag.FullName || tag.Name || '').toLowerCase();
	return name.includes(lowerQuery);
};

const filteredByKey = computed<Record<string, TagWithFrequency[]>>(() => {
	const result: Record<string, TagWithFrequency[]> = {};
	for (const category of resolvedCategories.value) {
		result[category.key] = category.tags.filter(
			tag => matchesSearch(tag, debouncedQuery.value)
		);
	}
	return result;
});

const getFilteredTags = (category: TagCategory): TagWithFrequency[] => {
	return filteredByKey.value[category.key] ?? [];
};

const hasVisibleTags = (category: TagCategory): boolean => {
	return getFilteredTags(category).length > 0;
};

const hasAnyTags = computed(() => {
	return resolvedCategories.value.some(c => c.tags.length > 0)
})

const isCategoryAllSelected = (category: TagCategory): boolean => {
	if (category.tags.length === 0) {
		return false;
	}
	return category.tags.every(t => selectedIds.value.has(t.Id));
};

const toggleTag = (tag: TagWithFrequency) => {
	const current = [...props.modelValue];
	const index = current.findIndex(t => t.Id === tag.Id);
	if (index >= 0) {
		current.splice(index, 1);
	} else {
		current.push(tag);
	}
	emit('update:modelValue', current);
};

const toggleCategory = (category: TagCategory) => {
	const visibleTags = category.tags;
	const allSelected = isCategoryAllSelected(category);
	let current = [...props.modelValue];

	if (allSelected) {
		const categoryIds = new Set(visibleTags.map(t => t.Id));
		current = current.filter(t => !categoryIds.has(t.Id));
	} else {
		const existingIds = new Set(current.map(t => t.Id));
		for (const tag of visibleTags) {
			if (!existingIds.has(tag.Id)) {
				current.push(tag);
			}
		}
	}

	emit('update:modelValue', current);
};

const resetSelection = () => {
	emit('update:modelValue', []);
	emit('reset');
};

const createTag = () => {
	const name = searchQuery.value.trim();
	if (!name) {
		return;
	}
	emit('create-tag', name.toLowerCase());

	clearSearch();
};
</script>

<style lang="scss" scoped>
@import '@/styles/color.scss';

.app-tag-selector {
	position: relative;
	background: $background-white;
	font-family: 'Roboto', sans-serif;
	display: flex;
	flex-direction: column;
	width: 308px;
	height: 422px;
	max-height: 100%;
	overflow: hidden;

	&--select {
		width: 616px;
	}

	@media (max-width: 768px) {
		width: 100% !important;
		height: 100% !important;
	}

	&__header {
		padding: 8px;
		display: flex;
		flex-direction: column;
		gap: 8px;
		flex-shrink: 0;
	}

	&__search {
		display: flex;
		align-items: center;
		gap: 8px;

		:deep(.input-wrapper) {
			flex: 1;
		}
	}

	&__close {
		display: flex;
		justify-content: center;
		flex-shrink: 0;
		width: 24px;
		height: 24px;

		&-icon {
			margin-top: -3px;
		}
	}

	&__reset {
		position: absolute;
		right: 14px;
		top: 47px;
		font-size: 12px;
		line-height: 16px;
		font-weight: 400;
		color: $color-text-secondary;
		cursor: pointer;
		white-space: nowrap;
		user-select: none;
		z-index: 1;

		&:hover {
			text-decoration: underline;
		}
	}

	&__body {
		padding: 0 8px;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		flex: 1;
		min-height: 0;

		&--columns {
			display: flex;
			flex-direction: column;
			overflow: hidden;
		}

		&--loading {
			position: relative;
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: 100px;
		}
	}

	&__columns {
		display: flex;
		flex-direction: row;
		gap: 1px;
		flex: 1;
		min-height: 0;
	}

	&__section {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
	}

	&__section--system {
		flex: 1 1 50%;
		border-right: 1px solid $color-divider;
		padding-right: 8px;
		overflow: hidden;
	}

	&__section-right {
		flex: 1 1 50%;
		display: flex;
		flex-direction: column;
		min-height: 0;
		padding-left: 8px;
	}

	&__section--entity,
	&__section--user {
		flex: 1 1 50%;
		min-height: 0;
		overflow: hidden;
	}

	&__section--entity {
		border-bottom: 1px solid $color-divider;
		padding-bottom: 8px;
		margin-bottom: 8px;
	}

	&__section--full {
		flex: 1 1 100%;
	}

	&__section-title {
		display: flex;
		align-items: center;
		padding: 0;
		margin-bottom: 8px;
		user-select: none;
		flex-shrink: 0;

		&-text {
			font-size: 12px;
			font-weight: 500;
			line-height: 16px;
			color: $color-text-secondary;
		}
	}

	&--select &__section-title {
		cursor: pointer;
	}

	&__empty {
		padding: 12px 0;
		text-align: center;
		font-size: 14px;
		color: $color-text-secondary;
		flex-shrink: 0;
	}
}
</style>
